import { graphql } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'

/**
 * The full mention shape the card tree consumes through `MentionContext`. It
 * selects every field the surrounding card / list / context / reply surfaces
 * read off a mention, so `MentionData` derives from one place instead of the
 * codegen `Mention` entity.
 *
 * Kept as a flat selection (rather than composing the colocated component
 * fragments) so `assets` is selected once — composing fragments that each
 * select `assets` produces an intersection type the shared asset fixtures
 * don't satisfy. The mention producers spread this and return its type; the
 * `assets` selection mirrors the shape the shared `EngagementMedia` seam and
 * the story fixtures expect — the asset omits `id`, and the nested image keeps
 * its `__typename`.
 *
 * Lives in a `fragments/` directory because codegen's document scanner only
 * globs `.tsx`, `use*.ts`, and `fragments/*.ts` — a plain `.ts` elsewhere
 * wouldn't be collected, and producers spreading `...MentionData_Mention`
 * would fail document validation.
 */
export const MentionData_Mention = graphql(/* GraphQL */ `
  fragment MentionData_Mention on Mention @_unmask {
    __typename
    id
    parentId
    channelId
    organizationId
    serviceType
    mentionType
    status
    text
    publicUrl
    publishedAt
    createdAt
    isOwnEngagement
    contentType
    reactions {
      type
    }
    suggestions {
      text
      source
    }
    author {
      __typename
      name
      username
      avatar
      letterAvatar {
        letter
        textColor
        backgroundColor
      }
    }
    assets {
      __typename
      mimeType
      thumbnail
      source
      type
      ... on ImageAsset {
        image {
          __typename
          altText
          width
          height
          isAnimated
        }
      }
      ... on VideoAsset {
        video {
          width
          height
        }
      }
    }
  }
`)

/** The full mention shape provided through `MentionContext`. */
export type MentionData = FragmentOf<typeof MentionData_Mention>
